import { useEffect, useRef, useState, type ReactNode } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { TextAlign } from '@tiptap/extension-text-align';
import { TableKit } from '@tiptap/extension-table';
import { Placeholder } from '@tiptap/extensions';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Brush,
  Code,
  Heading1,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Music,
  Quote,
  Redo2,
  Strikethrough,
  Table as TableIcon,
  Trash2,
  Underline,
  Undo2,
  Minus,
} from 'lucide-react';
import { uploadFile } from '../../api/endpoints';
import DrawingPad from './DrawingPad';

/** 正文中的音频块：<audio controls src="…" /> */
const AudioNode = Node.create({
  name: 'audio',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes: () => ({
    src: { default: null },
  }),
  parseHTML: () => [{ tag: 'audio[src]' }],
  renderHTML: ({ HTMLAttributes }) => [
    'audio',
    mergeAttributes(HTMLAttributes, { controls: true, preload: 'none' }),
  ],
});

interface RichEditorProps {
  value: string;
  onChange: (html: string) => void;
}

interface BtnProps {
  active?: boolean;
  disabled?: boolean;
  title: string;
  onClick: () => void;
  children: ReactNode;
}

function Btn({ active, disabled, title, onClick, children }: BtnProps) {
  return (
    <button
      type="button"
      className={`rt-btn ${active ? 'rt-btn-active' : ''}`}
      disabled={disabled}
      title={title}
      aria-label={title}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** 可视化富文本编辑器（TipTap）：图片、音频、表格、对齐、画图插入等 */
export default function RichEditor({ value, onChange }: RichEditorProps) {
  const [padOpen, setPadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Image,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TableKit.configure({ table: { resizable: true } }),
      Placeholder.configure({ placeholder: '开始撰写正文……支持插入图片、表格、音频与手绘插图' }),
      AudioNode,
    ],
    content: value || '',
    editorProps: {
      attributes: { class: 'prose rich-prose' },
    },
    onUpdate: ({ editor: current }) => {
      onChange(current.isEmpty ? '' : current.getHTML());
    },
  });

  // 外部 value 变化时同步进编辑器（避免光标跳动）
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if ((value || '') !== current && !editor.isFocused) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  const uploadAndInsert = async (file: File, kind: 'image' | 'audio') => {
    if (!editor) return;
    setUploading(true);
    try {
      const res = await uploadFile(file);
      if (kind === 'image') {
        editor.chain().focus().setImage({ src: res.url }).run();
      } else {
        editor.chain().focus().insertContent({ type: 'audio', attrs: { src: res.url } }).run();
      }
    } catch (error) {
      window.alert(error instanceof Error ? error.message : '上传失败，请稍后重试');
    } finally {
      setUploading(false);
    }
  };

  const handlePadSave = async (dataUrl: string) => {
    setPadOpen(false);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      await uploadAndInsert(new File([blob], 'drawing.png', { type: 'image/png' }), 'image');
    } catch {
      window.alert('插图上传失败，请重试');
    }
  };

  if (!editor) {
    return <div className="rich-editor rich-editor-loading">编辑器加载中……</div>;
  }

  const inTable = editor.isActive('table');

  return (
    <div className="rich-editor">
      <div className="rich-toolbar" role="toolbar" aria-label="正文格式工具栏">
        <div className="rt-group">
          <Btn title="撤销" onClick={() => editor.chain().focus().undo().run()}>
            <Undo2 size={15} />
          </Btn>
          <Btn title="重做" onClick={() => editor.chain().focus().redo().run()}>
            <Redo2 size={15} />
          </Btn>
        </div>
        <span className="rt-sep" aria-hidden="true" />
        <div className="rt-group">
          <Btn title="一级标题" active={editor.isActive('heading', { level: 1 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
            <Heading1 size={15} />
          </Btn>
          <Btn title="二级标题" active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
            <Heading2 size={15} />
          </Btn>
          <Btn title="三级标题" active={editor.isActive('heading', { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
            <Heading3 size={15} />
          </Btn>
        </div>
        <span className="rt-sep" aria-hidden="true" />
        <div className="rt-group">
          <Btn title="加粗" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
            <Bold size={15} />
          </Btn>
          <Btn title="斜体" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
            <Italic size={15} />
          </Btn>
          <Btn title="下划线" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <Underline size={15} />
          </Btn>
          <Btn title="删除线" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
            <Strikethrough size={15} />
          </Btn>
          <Btn title="行内代码" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>
            <Code size={15} />
          </Btn>
        </div>
        <span className="rt-sep" aria-hidden="true" />
        <div className="rt-group">
          <Btn title="左对齐" active={editor.isActive({ textAlign: 'left' })}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}>
            <AlignLeft size={15} />
          </Btn>
          <Btn title="居中" active={editor.isActive({ textAlign: 'center' })}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}>
            <AlignCenter size={15} />
          </Btn>
          <Btn title="右对齐" active={editor.isActive({ textAlign: 'right' })}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}>
            <AlignRight size={15} />
          </Btn>
          <Btn title="两端对齐" active={editor.isActive({ textAlign: 'justify' })}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}>
            <AlignJustify size={15} />
          </Btn>
        </div>
        <span className="rt-sep" aria-hidden="true" />
        <div className="rt-group">
          <Btn title="无序列表" active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}>
            <List size={15} />
          </Btn>
          <Btn title="有序列表" active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}>
            <ListOrdered size={15} />
          </Btn>
          <Btn title="引用" active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}>
            <Quote size={15} />
          </Btn>
          <Btn title="代码块" active={editor.isActive('codeBlock')}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
            <Code size={15} />
          </Btn>
          <Btn title="分割线" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
            <Minus size={15} />
          </Btn>
        </div>
        <span className="rt-sep" aria-hidden="true" />
        <div className="rt-group">
          <Btn title="插入链接"
            onClick={() => {
              const url = window.prompt('请输入链接地址（https://…）');
              if (url === null) return;
              if (!url.trim()) {
                editor.chain().focus().unsetLink().run();
                return;
              }
              editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
            }}>
            <Link2 size={15} />
          </Btn>
          <Btn title="插入图片" disabled={uploading} onClick={() => imageInputRef.current?.click()}>
            <ImagePlus size={15} />
          </Btn>
          <Btn title="插入音频" disabled={uploading} onClick={() => audioInputRef.current?.click()}>
            <Music size={15} />
          </Btn>
          <Btn title="手绘插图" disabled={uploading} onClick={() => setPadOpen(true)}>
            <Brush size={15} />
          </Btn>
          <Btn title="插入表格" onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
            <TableIcon size={15} />
          </Btn>
          {inTable && (
            <>
              <Btn title="在下方插入行" onClick={() => editor.chain().focus().addRowAfter().run()}>
                行+
              </Btn>
              <Btn title="删除当前行" onClick={() => editor.chain().focus().deleteRow().run()}>
                行-
              </Btn>
              <Btn title="在右侧插入列" onClick={() => editor.chain().focus().addColumnAfter().run()}>
                列+
              </Btn>
              <Btn title="删除当前列" onClick={() => editor.chain().focus().deleteColumn().run()}>
                列-
              </Btn>
              <Btn title="切换表头行" onClick={() => editor.chain().focus().toggleHeaderRow().run()}>
                表头
              </Btn>
              <Btn title="删除整个表格" onClick={() => editor.chain().focus().deleteTable().run()}>
                <Trash2 size={15} />
              </Btn>
            </>
          )}
          <Btn title="清除所选格式" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
            <Trash2 size={15} />
          </Btn>
        </div>
        {uploading && <span className="rt-uploading">上传中……</span>}
      </div>

      <EditorContent editor={editor} className="rich-content" />

      {padOpen && (
        <DrawingPad
          onCancel={() => setPadOpen(false)}
          onSave={(dataUrl) => void handlePadSave(dataUrl)}
        />
      )}

      <input
        ref={imageInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void uploadAndInsert(file, 'image');
          event.target.value = '';
        }}
      />
      <input
        ref={audioInputRef}
        type="file"
        accept="audio/*"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void uploadAndInsert(file, 'audio');
          event.target.value = '';
        }}
      />
    </div>
  );
}
